import { cn } from "@/lib/utils";
import { Trophy, Target, Shield, Lock, CheckCircle, ChevronRight } from "lucide-react";

interface Props {
  title: string;
  type: "midpoint" | "capstone" | "cross_course";
  badge?: string | null;
  passed?: boolean;
  locked?: boolean;
  questionsCount?: number;
  onClick: () => void;
}

export default function AssessmentCard({ title, type, badge, passed, locked, questionsCount, onClick }: Props) {
  const Icon = type === "capstone" ? Trophy : type === "cross_course" ? Shield : Target;
  const typeLabel = type === "capstone" ? "Capstone" : type === "cross_course" ? "Mastery Check" : "Checkpoint";
  const accentColor = type === "capstone" ? "accent" : "primary";

  return (
    <button
      onClick={onClick}
      disabled={locked}
      className={cn(
        "w-full text-left rounded-2xl border p-4 transition-all group",
        locked
          ? "border-border/30 bg-card/20 opacity-60 cursor-not-allowed"
          : passed
          ? "border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/50"
          : `border-${accentColor}/20 bg-${accentColor}/5 hover:border-${accentColor}/40 hover:shadow-[0_0_20px_hsl(var(--${accentColor})/0.1)]`
      )}
    >
      <div className="flex items-center gap-3">
        <div className={cn(
          "flex h-10 w-10 items-center justify-center rounded-xl",
          locked ? "bg-muted/30" :
          passed ? "bg-emerald-500/15" :
          `bg-${accentColor}/15`
        )}>
          {locked ? <Lock className="h-5 w-5 text-muted-foreground" /> :
           passed ? <CheckCircle className="h-5 w-5 text-emerald-400" /> :
           <Icon className={cn("h-5 w-5", type === "capstone" ? "text-accent" : "text-primary")} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={cn(
              "text-[10px] font-heading font-semibold uppercase tracking-wider",
              locked ? "text-muted-foreground" :
              passed ? "text-emerald-400" :
              type === "capstone" ? "text-accent" : "text-primary"
            )}>
              {typeLabel}
            </span>
            {passed && <span className="text-[10px] text-emerald-400">✓ Passed</span>}
          </div>
          <p className="text-sm font-heading font-semibold text-foreground truncate">{title}</p>
          {badge && !locked && (
            <p className="text-[10px] font-body text-muted-foreground mt-0.5">🏅 {badge}</p>
          )}
        </div>

        {!locked && (
          <ChevronRight className={cn(
            "h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5",
            passed ? "text-emerald-400" : "text-muted-foreground"
          )} />
        )}
      </div>
    </button>
  );
}
